import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {client} from "@/httpClient";
import {queryClient} from "@/queryClient";
import useCampaignManagementStore from "@/store/campaignManagement";
import {create} from "@/store/createStore";
import type {
  TCompanyGroup,
  TInvitableInfluencer,
  TInvitationData,
  TInviteCategory,
  TInvitationCategory as TInviteFilterBoundaries,
  TNonFunctionKeys,
  TPaginated,
} from "@/store/types";
import {type InfiniteData} from "@tanstack/react-query";
import type {AxiosResponse} from "axios";
import {debounce, omit, sumBy, uniqBy, type DebouncedFuncLeading} from "lodash";
import qs from "qs";
import {devtools} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";

type TFilters = {
  city: string;
  engagement_start: number;
  engagement_end: number;
  followers_start: number;
  followers_end: number;
  rating_start: number;
  rating_end: number;
  tiktok_last_period_video_count_start: number;
  tiktok_last_period_video_count_end: number;
  gender: string[];
  new: boolean;
  no_review: boolean;
  search: string | null;
  ordering: "-last_app_login" | null;
};

type TCategorySelectionState = {
  allPossibleInfluencers: number[];
  noLimitInfluencers: number[];
  filters: TFilters;
  searchParams: string | null;
};
type TUserFromTab = {
  userId: number;
  sourceTab: TInviteCategory;
  groupId?: number | null; // Only filled out when the influencer is selected by selecting the entire group
};

const initialFilterState = {
  followers__min: null,
  followers__max: null,
  engagement__min: null,
  engagement__max: null,
  rating__min: null,
  rating__max: null,
  tiktok_last_period_video_count__min: null,
  tiktok_last_period_video_count__max: null,
  followers__histogram: [],
  engagement__histogram: [],
  tiktok_last_period_video_count__histogram: [],
  rating__histogram: [],
  count: 0,
} as TInviteFilterBoundaries;

const initialSelectionState = {
  allPossibleInfluencers: [],
  noLimitInfluencers: [],
  selectedInfluencers: [],
  filters: {
    city: "",
    engagement_start: 0,
    engagement_end: 0,
    followers_start: 0,
    followers_end: 0,
    rating_start: 0,
    rating_end: 0,
    tiktok_last_period_video_count_start: 0,
    tiktok_last_period_video_count_end: 0,
    new: false,
    no_review: false,
    gender: [],
    search: null,
    ordering: null,
  },
  searchParams: null,
} as TCategorySelectionState;

type TState = {
  isOpen: boolean;
  selectedInfluencers: TUserFromTab[];
  selectedGroups: TCompanyGroup[];
  selectedCategory: TInviteCategory | null;
  categoryCounts: {[key in TInviteCategory]: number | null};
  invitableInfluencers: {[key in TInviteCategory]: TCategorySelectionState};
  filterBoundaries: {
    [key in TInviteCategory]: Omit<TInviteFilterBoundaries, "count">;
  };
  debouncedSearchParams: DebouncedFuncLeading<() => void>;
  startDebouncedSearch(timeout?: number): void;
  toggleInfluencerSelection(id: number, groupId?: number | null): void;
  toggleGroupSelection(group: TCompanyGroup): void;
  toggleGroupInfluencerSelection(id: number, group: TCompanyGroup): void;
  updateInfluencerGroupId(id: number, groupId: number | null): void;
  deselectAll(): void;
  selectAllInCategory(): void;
  selectOrdering(): (typeof initialSelectionState)["filters"]["ordering"];
  deselectAllInCategory(): void;
  inviteSelected(): Promise<void>;
  setFilterBoundariesAndCounts(data: TInvitationData): void;
  closeInviteModal(): void;
  buildSearchParams(): string;
  onCategoryChanged(newCategory: TInviteCategory): void;
  setSearchParams(searchParams: string): void;
  setMembersOfCategory(userIds: number[]): void;
  setNoLimitInfluencersOfCategory(userIds: number[]): void;
  setFilter<K extends keyof TFilters, V extends TFilters[K]>(key: K, value: V): void;
  getHistogram<
    K extends
      | "followers__histogram"
      | "engagement__histogram"
      | "rating__histogram"
      | "tiktok_last_period_video_count__histogram",
  >(
    key: K,
  ): TState["filterBoundaries"][TInviteCategory][K];
  getSelectedInfluencersCount(): number;
  setValue<K extends TNonFunctionKeys<TState>>(key: K, value: TState[K]): void;
};

const initialState = {
  isOpen: false,
  selectedInfluencers: [],
  selectedGroups: [],
  selectedCategory: null,
  categoryCounts: {
    FAVORITE: 0,
    FOLLOWS_YOU: 0,
    LIKED_YOU: 0,
    RECOMMENDED: 0,
    WORKED_WITH: 0,
    GROUPS: 0,
    ADMIN: 0,
    PRO_INFLUENCER: 0,
  },

  invitableInfluencers: {
    FAVORITE: initialSelectionState,
    FOLLOWS_YOU: initialSelectionState,
    LIKED_YOU: initialSelectionState,
    RECOMMENDED: initialSelectionState,
    GROUPS: initialSelectionState,
    WORKED_WITH: initialSelectionState,
    ADMIN: initialSelectionState,
    PRO_INFLUENCER: initialSelectionState,
  },

  filterBoundaries: {
    FAVORITE: initialFilterState,
    FOLLOWS_YOU: initialFilterState,
    LIKED_YOU: initialFilterState,
    RECOMMENDED: initialFilterState,
    GROUPS: initialFilterState,
    WORKED_WITH: initialFilterState,
    ADMIN: initialFilterState,
    PRO_INFLUENCER: initialFilterState,
  },
} as Pick<TState, TNonFunctionKeys<TState>>;

const useInfluencerInviteStore = create<TState>()(
  immer(
    devtools(
      (set, get) => ({
        ...initialState,
        debouncedSearchParams: debounce(() => {
          get().buildSearchParams();
        }, 1000),
        setValue: (key, value) =>
          set(
            state => {
              state[key] = value;
            },
            false,
            "useInfluencerInviteStore/setValue",
          ),
        deselectAll: () =>
          set(
            state => {
              state.selectedInfluencers = [];
              state.selectedGroups = [];
            },
            false,
            "useInfluencerInviteStore/deselectAll",
          ),
        selectAllInCategory: () => {
          set(
            state => {
              state.selectedInfluencers = uniqBy(
                state.selectedInfluencers.concat(
                  state.invitableInfluencers[get().selectedCategory!].allPossibleInfluencers.map(id => ({
                    userId: id,
                    sourceTab: get().selectedCategory!,
                  })),
                ),
                "userId",
              );
            },
            false,
            "useInfluencerInviteStore/selectAllInCategory",
          );
        },
        deselectAllInCategory: () =>
          set(
            state => {
              state.selectedInfluencers = state.selectedInfluencers.filter(
                (userData: TUserFromTab) =>
                  !state.invitableInfluencers[state.selectedCategory!].allPossibleInfluencers.includes(userData.userId),
              );
            },
            true,
            "useInfluencerInviteStore/deselectAllInCategory",
          ),
        buildSearchParams: () => {
          const filters = get().invitableInfluencers[get().selectedCategory!].filters;
          const isTiktok = useCampaignManagementStore.getState().campaign.tiktok;

          const omittedParams = omit(
            filters,
            isTiktok
              ? ["engagement_start", "engagement_end"]
              : ["tiktok_last_period_video_count_start", "tiktok_last_period_video_count_end"],
          );

          return qs.stringify(omittedParams, {arrayFormat: "comma"});
        },
        setFilter: (key, value) => {
          set(
            state => {
              state.invitableInfluencers[get().selectedCategory!].filters[key] = value;
            },
            false,
            "useInfluencerInviteStore/setFilter",
          );

          get().startDebouncedSearch();
        },
        setSearchParams: searchParams =>
          set(
            state => {
              state.invitableInfluencers[get().selectedCategory!].searchParams = searchParams;
            },
            false,
            "useInfluencerInviteStore/setSearchParams",
          ),
        setMembersOfCategory: userIds =>
          set(
            state => {
              state.invitableInfluencers[state.selectedCategory!].allPossibleInfluencers = userIds;
            },
            false,
            "useInfluencerInviteStore/setMembersOfCategory",
          ),
        setNoLimitInfluencersOfCategory: userIds =>
          set(
            state => {
              state.invitableInfluencers[state.selectedCategory!].noLimitInfluencers = userIds;
            },
            false,
            "useInfluencerInviteStore/setNoLimitInfluencersOfCategory",
          ),

        setFilterBoundariesAndCounts: data => {
          set(
            state => {
              state.categoryCounts = {
                FAVORITE: data.favourite_influencers.count,
                FOLLOWS_YOU: data.influencers_following_you.count,
                LIKED_YOU: data.liked_your_campaign.count,
                WORKED_WITH: data.previously_worked_with.count,
                RECOMMENDED: data.recommended_influencers.count,
                GROUPS: data.groups.count,
                ADMIN: data.all_invitable_influencers.count,
                PRO_INFLUENCER: data.all_invitable_pro_influencers_data.count,
              };
              state.filterBoundaries = {
                FAVORITE: data.favourite_influencers,
                FOLLOWS_YOU: data.influencers_following_you,
                LIKED_YOU: data.liked_your_campaign,
                WORKED_WITH: data.previously_worked_with,
                RECOMMENDED: data.recommended_influencers,
                GROUPS: get().filterBoundaries.GROUPS,
                ADMIN: data.all_invitable_influencers,
                PRO_INFLUENCER: data.all_invitable_pro_influencers_data,
              };
              let k: TInviteCategory;
              for (k in state.filterBoundaries) {
                state.invitableInfluencers[k].filters.engagement_end = state.filterBoundaries[k].engagement__max ?? 0;
                state.invitableInfluencers[k].filters.engagement_start = state.filterBoundaries[k].engagement__min ?? 0;

                state.invitableInfluencers[k].filters.followers_end = state.filterBoundaries[k].followers__max ?? 0;
                state.invitableInfluencers[k].filters.followers_start = state.filterBoundaries[k].followers__min ?? 0;

                state.invitableInfluencers[k].filters.rating_end = state.filterBoundaries[k].rating__max ?? 0;
                state.invitableInfluencers[k].filters.rating_start = state.filterBoundaries[k].rating__min ?? 0;

                state.invitableInfluencers[k].filters.tiktok_last_period_video_count_end =
                  state.filterBoundaries[k].tiktok_last_period_video_count__max ?? 0;
                state.invitableInfluencers[k].filters.tiktok_last_period_video_count_start =
                  state.filterBoundaries[k].tiktok_last_period_video_count__min ?? 0;
              }
            },
            false,
            "useInfluencerInviteStore/setFilterBoundariesAndCounts",
          );
        },
        inviteSelected: async () => {
          const campaignId = useCampaignManagementStore.getState().campaign.id!;

          // First we invite the individual influencers
          await client.post(CAMPAIGN_KEYS.mCampaignBulkInvite(campaignId)[0], {
            invites: get()
              .selectedInfluencers.filter(i => !i.groupId)
              .map(i => `${i.userId}:${i.sourceTab}`),
          });

          // Then we invite the groups that are fully selected (the backend will ignore influencers that are already invited by the first API call)
          // TODO: refactor this into a single API call when the backend supports multiple group_id parameters at the same time
          await Promise.all(
            get().selectedGroups.map(g =>
              client.post(CAMPAIGN_KEYS.mCampaignBulkInvite(campaignId)[0], {group_id: g.id}),
            ),
          );
        },
        onCategoryChanged: newCategory => {
          if (newCategory === get().selectedCategory) return;
          set(
            state => {
              state.isOpen = true;
              state.selectedCategory = newCategory;
            },
            false,
            "useInfluencerInviteStore/onCategoryChanged",
          );
          get().startDebouncedSearch(0);
        },
        toggleInfluencerSelection: (id, groupId) => {
          if (
            get()
              .selectedInfluencers.map(u => u.userId)
              .includes(id)
          )
            set(
              state => {
                state.selectedInfluencers = state.selectedInfluencers.filter(item => item.userId !== id);
              },
              false,
              "useInfluencerInviteStore/toggleInfluencerSelection",
            );
          else
            set(
              state => {
                state.selectedInfluencers.push({
                  userId: id,
                  sourceTab: get().selectedCategory!,
                  groupId,
                });
              },
              false,
              "useInfluencerInviteStore/toggleInfluencerSelection",
            );
        },
        toggleGroupSelection: group => {
          const groupIndex = get().selectedGroups.findIndex(g => g.id === group.id);

          if (groupIndex !== -1) {
            set(
              state => {
                state.selectedGroups = state.selectedGroups.filter(g => g.id !== group.id);
              },
              false,
              "useInfluencerInviteStore/toggleGroupSelection",
            );
          } else {
            set(
              state => {
                state.selectedGroups.push(group);
              },
              false,
              "useInfluencerInviteStore/toggleGroupSelection",
            );
          }
        },
        toggleGroupInfluencerSelection: (id, group) => {
          if (!!get().selectedGroups.find(g => g.id === group.id)) {
            get().toggleGroupSelection(group);
          }

          get().toggleInfluencerSelection(id);
        },
        updateInfluencerGroupId: (id, groupId) => {
          set(
            state => {
              state.selectedInfluencers = get().selectedInfluencers.map(i => (i.userId === id ? {...i, groupId} : i));
            },
            false,
            "useInfluencerInviteStore/updateInfluencerGroupId",
          );
        },
        getHistogram: key => get().filterBoundaries[get().selectedCategory!][key],
        closeInviteModal: () => {
          ["LIKED_YOU", "WORKED_WITH", "FAVORITE", "FOLLOWS_YOU", "RECOMMENDED"].map(key =>
            queryClient.removeQueries({queryKey: [key]}),
          );

          set(
            state => {
              let k: TInviteCategory;
              for (k in state.filterBoundaries) {
                state.invitableInfluencers[k].filters.engagement_end = state.filterBoundaries[k].engagement__max ?? 0;
                state.invitableInfluencers[k].filters.engagement_start = state.filterBoundaries[k].engagement__min ?? 0;

                state.invitableInfluencers[k].filters.followers_end = state.filterBoundaries[k].followers__max ?? 0;
                state.invitableInfluencers[k].filters.followers_start = state.filterBoundaries[k].followers__min ?? 0;

                state.invitableInfluencers[k].filters.rating_end = state.filterBoundaries[k].rating__max ?? 0;
                state.invitableInfluencers[k].filters.rating_start = state.filterBoundaries[k].rating__min ?? 0;

                state.invitableInfluencers[k].filters.tiktok_last_period_video_count_end =
                  state.filterBoundaries[k].tiktok_last_period_video_count__max ?? 0;
                state.invitableInfluencers[k].filters.tiktok_last_period_video_count_start =
                  state.filterBoundaries[k].tiktok_last_period_video_count__min ?? 0;
              }
              state.selectedInfluencers = [];
              state.selectedGroups = [];
              state.isOpen = false;
              state.selectedCategory = null;
            },
            false,
            "closeInviteModal/reset",
          );
        },
        startDebouncedSearch: (timeout = 1000) => {
          get().debouncedSearchParams.cancel();

          const searchParams = get().buildSearchParams();
          const cachedQuery = queryClient.getQueryData<InfiniteData<AxiosResponse<TPaginated<TInvitableInfluencer>>>>(
            CAMPAIGN_KEYS.qCampaignInvitableInfluencers({
              campaignId: Number(useCampaignManagementStore.getState().campaign.id),
              category: get().selectedCategory!,
              searchParams,
            }),
          );

          if (!!cachedQuery?.pages) {
            timeout = 0;
          }

          set(
            state => {
              state.debouncedSearchParams = debounce(() => {
                get().setSearchParams(searchParams);
              }, timeout);
            },
            false,
            "useInfluencerInviteStore/setDebouncedSearchParams",
          );

          get().debouncedSearchParams();
        },
        getSelectedInfluencersCount: () => {
          const influencerCount = get().selectedInfluencers.filter(i => !i.groupId).length;
          const groupInfluencerCount = sumBy(get().selectedGroups, g => g.verified_influencers_count ?? 0);

          return influencerCount + groupInfluencerCount;
        },
        selectOrdering: () => get().invitableInfluencers[get().selectedCategory!].filters.ordering,
      }),
      {
        name: "useInfluencerInviteStore",
      },
    ),
  ),
);

export default useInfluencerInviteStore;
